import { PrimaryButton, SecondaryButton } from '@/components/buttons';
import { Loading } from '@/components/loading';
import { Body2, H2 } from '@/components/typography';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { routes } from '@/modules/Router';
import { useValidateResetToken } from '@/modules/Users/ResetToken';
import { useChangePassword } from '@/modules/Users/Users.api';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm, UseFormRegister } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { changePasswordInputSchema, ChangePasswordInputSchema, ResetType } from './Users.schema';

function ChangePasswordInput({
  fieldName,
  label,
  placeholder,
  register,
  error,
}: {
  fieldName: keyof ChangePasswordInputSchema;
  label: string;
  placeholder: string;
  register: UseFormRegister<ChangePasswordInputSchema>;
  error?: string;
}) {
  return (
    <div>
      <label htmlFor={fieldName} className={cn('block text-sm font-medium text-gray-700')}>
        {label}
      </label>
      <div className={cn('mt-1')}>
        <Input {...register(fieldName)} type="password" id={fieldName} placeholder={placeholder} className="pr-10" aria-invalid={!!error} />
      </div>
      {error && <div className={cn('text-error-500 bg-error-50 border border-error-200 rounded px-2 py-1 mt-2 text-sm')}>{error}</div>}
    </div>
  );
}

interface Props {
  reset_type: ResetType.PASSWORD | ResetType.ONBOARDING;
}

export function ChangePasswordForm({ reset_type }: Props) {
  const { register, handleSubmit, formState } = useForm<ChangePasswordInputSchema>({
    mode: 'all',
    resolver: zodResolver(changePasswordInputSchema),
  });
  const mutation = useChangePassword();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [termsAccepted, setTermsAccepted] = useState(false);
  const token = searchParams.get('token') || '';
  const validToken = useValidateResetToken({ token, reset_type });
  const isOnboarding = reset_type === ResetType.ONBOARDING;

  const title = reset_type === ResetType.PASSWORD ? 'Change Password' : 'Welcome to Agricarbon!';
  const subTitle =
    reset_type === ResetType.PASSWORD
      ? 'Use the form below to change your password.'
      : 'First we need you to set up a password using the form below.';
  const buttonText = reset_type === ResetType.PASSWORD ? 'Change Password' : 'Set Password';

  if (validToken === false) {
    throw new Error('Invalid Token');
  }

  const onSubmit = (data: ChangePasswordInputSchema) => {
    mutation.mutate(
      {
        new_password: data.new_password,
        token,
        reset_type,
      },
      {
        onSuccess: (data) => {
          toast.success('Password changed!', {
            autoClose: 5000,
          });
          if (data.otp_status) {
            navigate(routes.login);
          } else {
            navigate(`${routes.otp_setup}?token=${data.otp_token}`);
          }
        },
        onError: (error) => {
          toast.error(error.message, {
            autoClose: 3000,
          });
          console.error('Submitting Change Password failed. Error message:', error);
        },
      }
    );
  };

  const onCancel = () => {
    navigate(routes.login);
  };

  return (
    <div className={cn('h-full flex items-center mt-10')}>
      <form onSubmit={handleSubmit(onSubmit)} className={cn('flex flex-col gap-6 w-[640px] shadow rounded-lg p-20')}>
        <H2 className={cn('text-center')}>{title}</H2>
        <Body2 className={cn('text-center text-neutral-500')}>{subTitle}</Body2>
        <ChangePasswordInput
          register={register}
          error={formState.errors.new_password?.message}
          fieldName="new_password"
          label="New Password"
          placeholder="Enter a new password"
        />
        <ChangePasswordInput
          register={register}
          error={formState.errors.confirm_password?.message}
          fieldName="confirm_password"
          label="Confirm Password"
          placeholder="Confirm a new password"
        />
        {isOnboarding && (
          <div className={cn('flex items-start gap-3')}>
            <input
              type="checkbox"
              id="terms_accepted"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              className={cn('mt-1 h-4 w-4 shrink-0 cursor-pointer accent-primary-600')}
            />
            <label htmlFor="terms_accepted" className={cn('text-sm text-neutral-600 leading-snug cursor-pointer')}>
              Please tick to confirm you have read and accept the{' '}
              <a
                href="https://www.agricarbon.co.uk/terms-and-conditions"
                target="_blank"
                rel="noopener noreferrer"
                className={cn('text-primary-600 underline hover:text-primary-800')}
              >
                Portal Terms and Conditions
              </a>{' '}
              and the{' '}
              <a
                href="https://www.agricarbon.co.uk/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className={cn('text-primary-600 underline hover:text-primary-800')}
              >
                Agricarbon Privacy Policy
              </a>
            </label>
          </div>
        )}
        <div className={cn('flex gap-6')}>
          {reset_type === ResetType.PASSWORD && (
            <SecondaryButton onClick={onCancel} disabled={mutation.status === 'pending'}>
              Back to Login
            </SecondaryButton>
          )}
          <PrimaryButton type="submit" disabled={!formState.isValid || mutation.status === 'pending' || (isOnboarding && !termsAccepted)}>
            {mutation.status === 'pending' ? (
              <div className={cn('w-full flex justify-center items-center')}>
                <Loading size={'small'} inline />
              </div>
            ) : (
              buttonText
            )}
          </PrimaryButton>
        </div>
      </form>
    </div>
  );
}
